import { InputBase } from "./InputBase";
import { MultipleMarkdown } from "./MarkDown";

export interface InputPageResult {
  readonly inputs: InputBase[];
  readonly title: string;
  readonly multiPartRelationships: Record<string, string>;
  readonly hideProfileData: boolean;
  readonly notesAtTopOfPage: MultipleMarkdown;
  readonly notesAtBottomOfPage: MultipleMarkdown;
  readonly defaultValues: { string: string };
}

export enum SnackbarColors {
  info = "info",
  warn = "warning",
  error = "error",
  success = "success"
}

export interface Snackbar {
  readonly key: string;
  readonly top: boolean;
  readonly bottom: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly color: SnackbarColors;
  readonly transition: string;
  readonly message: string;
  show: boolean;
}

export interface SnackbarBody {
  readonly text: string;
  readonly top?: boolean;
  readonly bottom?: boolean;
  readonly left?: boolean;
  readonly right?: boolean;
  readonly color?: SnackbarColors;
  readonly transition?: string;
  readonly timeout?: [number, string];
  // eslint-disable-next-line
  [key: string]: any;
}

export enum PageTypes {
  NewMaximoObject = "NewMaximoObject",
  UserProfile = "UserProfile",
  EditPage = "EditPage"
}

export interface UpdateInputRequest {
  inputId: string;
  newValue: string | boolean;
  pageName: string;
  pageType: string;
  url?: string;
  isRegistration?: boolean;
  callbacks: { [key: number]: Function };
}

export interface PersonModel {
  personAttributes: Record<string, string>;
  requiredAttributes: string[];
  timezone?: string;
  personId?: string;
  language?: string;
}

export interface LookupTableRequestState {
  // Primarily used for holding data related to pagination
  // in the data table component.
  href?: string;
  totalCount?: number;
  pagenum?: number;
  totalPages?: number;
  pageSize?: number;
}

export interface LookupTableDefaultFilters {
  // Used to hold data needed to enable default filtering or
  // using a value from an input form to provide default filtering
  // in the data table component.
  defaultValues?: { [key: string]: string };
  getValueFrom?: string[];
}

export interface LookupTableSearchProperties {
  defaultFilters: LookupTableDefaultFilters;
  customFilters: { [key: string]: string };
}

export interface LookupTable {
  isLoading: boolean;
  headers: Array<object>;
  // eslint-disable-next-line
  tableData: { [key: string]: any }[];
  show: boolean;
  error: boolean;
  inputId: string;
  lookupTableRequestState: LookupTableRequestState;
  searchFields: { [key: string]: string };
  valueToSubmit: string;
}

export interface LookupTableDataResponse {
  // eslint-disable-next-line
  tableData: { [key: string]: any }[];
  fields: string[];
  headers: Array<object>;
  lookupTableRequestState: LookupTableRequestState;
  valueToSubmit: string;
  lookupTableSearchDependencies: LookupTableSearchProperties;
}

export interface LookupTableInput {
  maxAttribute?: string;
  pageName: string;
  pageNumber: number;
  pageType: PageTypes;
  searchParams: { [key: string]: string | number | boolean };
}

export interface GetLookupTableInput extends LookupTableInput {
  lookupTableUrl?: string;
  formId?: string;
  ignoreDefaultFiltering?: boolean;
}

export interface GetInquiryTableInput extends LookupTableInput {
  searchNumber: number;
}

export interface UserSessionData {
  sessionHash: string;
  lastLoggeedInTime?: Date;
  userIsAuthenticated: boolean;
  person: PersonModel;
}

export interface UserData {
  userSessionData: UserSessionData;
  person: PersonModel;
}

export interface UiButton {
  label: string;
  type: string;
  value: string;
}

export interface ButtonsResponse {
  buttons: UiButton[];
  title: string;
  editPageName?: string;
  searchByIdConfig: SearchByIdConfig;
}

export interface SearchByIdConfig {
  useDefaultSite: boolean; // Used to toggle the visibility of the site id dropdown
  searchInput: SearchInput;
  siteIdDropdownLabel: string;
  sites: { [key: string]: string }[];
  tableData: { [key: string]: string };
  allowSearchAllSites: boolean; // Adds an "ALL SITES" item to the site id dropdown
  valueToSubmit: string;
}

export interface SearchInput {
  maxAttribute: string;
  label: string;
}

export interface InquiryTableResponse {
  // tableData contains all of the data that will be displayed in the table. Since the inquiry table could
  // be built with many different combinations of maxattributes, the keys are not garaunteed to be the same.
  // eslint-disable-next-line
  tableData: { [key: string]: any }[];
  fields: string[];
  headers: string[];
  valueToSubmit: string;
  lookupTableRequestState: LookupTableRequestState;
  lookupTableSearchDependencies: LookupTableSearchProperties;
  details: Details[];
}

export interface Details {
  label: string;
  maxAttr: string;
}

export interface RequestTypes {
  label: string;
  id: string;
}

export interface WorklogsConfig {
  addNewLogs: boolean;
  showWorklogs: boolean;
  logTypeOptions: logTypes[];
  worklogs: any;
  worklogDefaultValues: { [key: string]: string }[];
  multiPartRelationships: Map<string, string>;
  newWorklogInputs: InputBase[]; // for adding worklogs
  detailsInputs: InquiryDetails[]; // for showing worklog details
  worklogDetailsColumns: InquiryDetails[]; // for showing worklog details
  worklogColumns: dataTableHeader[]; // key is field, value is header for worklog table
  readonly notesAtTopOfForm: MultipleMarkdown;
  readonly notesAtBottomOfForm: MultipleMarkdown;
}

export interface InquiryDetailsResponse {
  title: string;
  worklogDetails: WorklogsConfig;
  details: InquiryDetails[]; // for showing details of a row in the inquiry table
  worklogDetailsColumns: InquiryDetails[];
  readonly notesAtTop: MultipleMarkdown;
  readonly notesAtBottom: MultipleMarkdown;
}

export interface FileToUpload {
  name: string;
  content: string;
  type: string;
  description: string;
  size: number;
}

export interface SearchDependenciesArgs {
  formId: string;
  maxAttribute: string;
  searchDependencies: LookupTableSearchProperties;
}

export interface WorklogDetailsInput {
  pageName: string;
  worklogBody: { [key: string]: string };
}

export interface RequestBodyErrorPayload {
  formId: string;
  payload: { [key: string]: boolean };
}

export interface LoginPageData {
  loginPlaceHolderText: string;
  loginType: string;
  textAboveInput: MultipleMarkdown;
  textBelowInput: MultipleMarkdown;
  icon: string;
  userIsLoggedIn: boolean;
  toastColor: string;
  toastMessage: string;
  isRegistrationDisabled: boolean;
}

export interface NewRequestResponse {
  editPageName: string;
  uniqueId: string;
  originalSnapshot: Record<string, unknown>;
  discrepancies: Record<string, unknown>;
}

export type tableItem = number | string | boolean | Date | Blob;
// eslint-disable-next-line
export type expandedItem = { item: any; value: boolean };
export type dataTableHeader = { text: string; value: string };
export type logTypes = { [key: string]: string }[];

export interface InquiryDetails {
  maxAttribute: string;
  label: string;
  value: string;
  // isError should only need to be used on the EditPage after submitting a new
  // request to indicate a discrepancy from Maximo.
  isError?: boolean;
  inputNotesAbove: MultipleMarkdown;
  inputNotesBelow: MultipleMarkdown;
}
